import React, { useState } from 'react';
import styles from './StaticPage.module.css';
import { ChevronDown, ChevronUp, Award, Shield, Star, Radio, Target, Users, Hexagon, TrendingUp, Coins, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getLevel } from '../utils/levels';

const GeneralOrgPoints: React.FC = () => {
  const [openIds, setOpenIds] = useState<string[]>([]);
  const { user } = useAuth();

  // Get org points directly from user object
  const orgPoints = user?.orgPoints || 0;

  const toggle = (id: string) => {
    setOpenIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openAndFocus = (id: string) => {
    setOpenIds((prev) => prev.includes(id) ? prev : [...prev, id]);
    const el = document.getElementById(`benefits-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const levelBenefits = [
    { id: 'lvl0', level: 0, title: 'Recruit', range: '0–10', icon: Target, benefits: [
      'Welcome to TFIC! You\'re just getting started',
      'Default Recruit badge and basic profile card',
      'Begin building your reputation through missions and events',
      'Full access to participate in org activities'
    ]},
    { id: 'lvl1', level: 1, title: 'Member', range: '10–100', icon: Shield, benefits: [
      'Member badge - recognized as an active contributor',
      'Mission system access unlocked',
      'Profile changes from Recruit to Member status',
      'Mission history tracking begins'
    ]},
    { id: 'lvl2', level: 2, title: 'Operator', range: '100–500', icon: Radio, benefits: [
      'Operator badge with enhanced profile styling',
      'Discord role highlight showing your active status',
      'Operator icon displayed on your mission records',
      'Growing reputation as an experienced member'
    ]},
    { id: 'lvl3', level: 3, title: 'Specialist', range: '500–1,000', icon: Target, benefits: [
      'Specialist badge with enhanced border or profile flair',
      'Optional profile motto/specialization field unlocked',
      'May co-host events or squads with leadership approval',
      'Recognized as a dedicated contributor'
    ]},
    { id: 'lvl4', level: 4, title: 'Veteran', range: '1,000–2,500', icon: Star, benefits: [
      'Veteran badge with premium banner theme',
      'Listed on Veteran Showcase section',
      'Eligible to apply as mission validator (approval required)',
      'First major milestone showing sustained commitment'
    ]},
    { id: 'lvl5', level: 5, title: 'Elite', range: '2,500–5,000', icon: Award, benefits: [
      'Elite badge with gold-accent profile frame',
      'Can flag select missions as Elite Logs (notable entries)',
      'Elevated visibility on mission-related pages',
      'Recognized as a high-value contributor'
    ]},
    { id: 'lvl6', level: 6, title: 'Master', range: '5,000–10,000', icon: Hexagon, benefits: [
      'Master badge with distinct premium frame',
      'Custom profile themes/backgrounds unlocked',
      'Featured as Master Contributor on recognition pages',
      'Mission streaks and contribution stats highlighted'
    ]},
    { id: 'lvl7', level: 7, title: 'Commander', range: '10,000–25,000', icon: Users, benefits: [
      'Commander badge with high-prestige decorative frame',
      'Optional "Commander-tier Member" tag in views',
      'May be considered for temporary squad lead roles (approval required)',
      'May assist planners or staff in running larger operations'
    ]},
    { id: 'lvl8', level: 8, title: 'Ace', range: '25,000–50,000', icon: TrendingUp, benefits: [
      'Ace badge with premium banner theme',
      'Highlighted on Achievement Wall or "Ace & Above" section',
      'Can submit screenshots/media for mission archives (staff-reviewed)',
      'Ace Highlights subsection on profile for notable missions'
    ]},
    { id: 'lvl9', level: 9, title: 'Legend', range: '50,000–100,000', icon: Star, benefits: [
      'Legend badge with legendary frame design',
      'Featured on Legend Board and org history pages',
      'Legend mission archives section on profile',
      'Seen as one of the long-standing pillars of TFIC'
    ]},
    { id: 'lvl10', level: 10, title: 'Paragon', range: '100,000+', icon: Award, benefits: [
      'Paragon badge - highest honor achievable within TFIC',
      'Paragon Hall placement (hall-of-fame style listing)',
      'Mini service profile with bio, contributions, and major campaigns',
      'Lifetime Paragon Contributor identity marker'
    ]},
  ];

  return (
    <div className={styles.pageContainer}>
      <div className={styles.heroWithStats}>
        <div className={styles.card}>
          <h1 className={styles.pageTitle}>Organization Points & Levels</h1>
          <p className={styles.heroSubtitle}>
            Earn ORG Points through missions, events, and contributions to progress through 11 levels from Recruit to Paragon.
            Levels represent your <strong>reputation, recognition, and commitment</strong> to TFIC.
          </p>
          <div className={styles.importantNote}>
            <strong>Important:</strong> Levels do NOT grant command authority or block access to missions and events. 
            They unlock identity rewards, profile customization, and <em>eligibility</em> for special roles (which leadership assigns). 
            Everyone can participate regardless of level - this system recognizes loyalty and contribution, not power.
          </div>
        </div>
        
        {user && (
          <div className={styles.userStatsCard}>
            <div className={styles.statsHeader}>Your Progress</div>
            <div className={styles.pointsDisplay}>
              <div className={styles.pointsValue}>{orgPoints.toLocaleString()}</div>
              <div className={styles.pointsLabel}>ORG Points</div>
            </div>
            <div className={styles.levelDisplay}>
              <div className={styles.currentLevel}>
                Level {getLevel(orgPoints).level}
              </div>
              <div className={styles.levelBadge}>
                {levelBenefits[getLevel(orgPoints).level]?.title || 'Recruit'}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.card}>
        <h2 className={styles.sectionHeader}>
          <TrendingUp size={24} />
          Progression Path
        </h2>
        <p className={styles.sectionDescription}>
          Click any level to view its benefits and requirements.
        </p>
        <div className={styles.levelsGrid}>
          {levelBenefits.map(({ id, level, title, range, icon: Icon }) => (
            <button 
              key={id}
              className={styles.levelCard} 
              onClick={() => openAndFocus(id)}
            >
              <div className={styles.levelLabel}>Level {level}</div>
              <Icon className={styles.levelCardIcon} size={42} />
              <div className={styles.levelTitle}>{title}</div>
              <div className={styles.levelRange}>{range} points</div>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.card}>
        <h2 className={styles.sectionHeader}>
          <Award size={24} /> Level Benefits
        </h2>
        <p className={styles.sectionDescription}>
          Each level unlocks new privileges, mission access, and recognition within the organization.
        </p>
        <div className={styles.accordion}>
          {levelBenefits.map(({ id, level, title, range, benefits, icon: Icon }) => {
            const open = openIds.includes(id);
            return (
              <div key={id} className={styles.accordionItem} id={`benefits-${id}`}>
                <button 
                  className={styles.accordionHeader} 
                  onClick={() => toggle(id)}
                >
                  <div className={styles.accordionHeaderContent}>
                    <Icon size={20} className={styles.accordionIcon} />
                    <span className={styles.accordionTitle}>
                      <strong>Level {level}: {title}</strong>
                      <span className={styles.accordionRange}>{range} points</span>
                    </span>
                  </div>
                  {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {open && (
                  <div className={styles.accordionBody}>
                    <ul className={styles.benefitsList}>
                      {benefits.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.card}> 
        <h2 className={styles.sectionHeader}>
          <Target size={24} /> How to Earn Points
        </h2>
        <p className={styles.sectionDescription}>
          ORG Points are awarded for active participation and contributions to TFIC. Here are all the ways you can earn:
        </p>
        <div className={styles.earnPointsGrid}>
          <div className={styles.earnCard}>
            <Target className={styles.earnCardIcon} size={32} />
            <h3>Complete Missions</h3>
            <p><strong>Base Reward:</strong> Points vary by mission difficulty and requirements</p>
            <p><strong>Medical Bonus:</strong> +50 points for any medical-related mission</p>
            <p><strong>Badges:</strong> Earn special badges that may grant additional points</p>
            <p><strong>Bonus Points:</strong> Mission creators can add extra rewards for exceptional completion</p>
          </div>
          <div className={styles.earnCard}>
            <Radio className={styles.earnCardIcon} size={32} />
            <h3>Attend Events</h3>
            <p><strong>After Action Reports:</strong> Leadership awards points through AARs after each event</p>
            <p><strong>Typical Award:</strong> 50 points per event (varies by event type and role)</p>
            <p><strong>RSVP Tracking:</strong> Your attendance is logged and recognized</p>
            <p><strong>Walk-ins Welcome:</strong> Even if you didn't RSVP, you can still earn points</p>
          </div>
          <div className={styles.earnCard}>
            <Users className={styles.earnCardIcon} size={32} />
            <h3>Join Departments</h3>
            <p><strong>Position Award:</strong> Get 25 points when approved for any department position</p>
            <p><strong>Department Types:</strong> Security, Operations, Medical, Intelligence, Logistics, and more</p>
            <p><strong>Ongoing Contribution:</strong> Additional recognition for active department participation</p>
          </div>
          <div className={styles.earnCard}>
            <Star className={styles.earnCardIcon} size={32} />
            <h3>Special Recognition</h3>
            <p><strong>Leadership Awards:</strong> Manual point awards for exceptional contributions</p>
            <p><strong>Mentorship:</strong> Helping new members learn and integrate</p>
            <p><strong>Recruitment:</strong> Bringing quality members to the organization</p>
            <p><strong>Content Creation:</strong> Guides, videos, documentation, and training materials</p>
          </div>
        </div>
      </div>
      
      <div className={styles.card}>
        <h2 className={styles.sectionHeader}>
          <Coins size={24} /> Reward Notifications
        </h2>
        <p className={styles.sectionDescription}>
          When you earn points, you'll receive toast notifications showing what you earned and why. If you earn points while offline, 
          you'll see a celebration toast when you log back in with all the details of what you earned.
        </p>
        <div className={styles.featureList}>
          <div className={styles.featureItem}>
            <CheckCircle className={styles.featureIcon} size={20} />
            <span><strong>Instant Feedback:</strong> Get notified immediately when missions are verified</span>
          </div>
          <div className={styles.featureItem}>
            <CheckCircle className={styles.featureIcon} size={20} />
            <span><strong>Offline Rewards:</strong> See all points earned while away with detailed breakdown</span>
          </div>
          <div className={styles.featureItem}>
            <CheckCircle className={styles.featureIcon} size={20} />
            <span><strong>Level Celebrations:</strong> Special notifications when you rank up to a new level</span>
          </div>
          <div className={styles.featureItem}>
            <CheckCircle className={styles.featureIcon} size={20} />
            <span><strong>Transaction History:</strong> View your complete earning history with timestamps and sources</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralOrgPoints;
